import React from 'react';
import { Plus, Trash2, Type } from 'lucide-react';
import type { NavbarLogo, Page, Component } from '../ComponentEditModal.types';
import type {
  NavigationMenuChildLink,
  NavigationMenuData,
  NavigationMenuLink,
} from '../../../schemas/validation';
import { LogoUploadModal } from '../../LogoUploadModal';
import { LogoPreview, NoLogoSection, AlignmentSection } from './NavbarSubComponents';
import { PageSelector } from '../shared/PageSelector';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import {
  applyLegacyBothNavbarMigration,
  isLegacyBothLogo,
  markLegacyBothNoticeSeen,
} from '../../../utils/navbarLogo';
import {
  isPlaceholderMenuLinkText,
  menuLinkUpdateForUrl,
  pageTitleForMenuUrl,
} from '../../../utils/menuLinkLabel';
import { NavigationMenuSubLinks } from './NavigationMenuSubLinks';
import { reorderSubLinks, sortSubLinks } from './reorderSubLinks';

const MAX_PARENTS = 12;
const MAX_CHILDREN = 10;

interface NavigationMenuEditorProps {
  formData: NavigationMenuData;
  setFormData: (data: NavigationMenuData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  siteId?: string;
  isLoading?: boolean;
  isLogoModalOpen: boolean;
  setIsLogoModalOpen: (open: boolean) => void;
  currentPageComponents?: Component[];
}

function nextId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export const NavigationMenuEditor: React.FC<NavigationMenuEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  siteId,
  isLoading = false,
  isLogoModalOpen,
  setIsLogoModalOpen,
  currentPageComponents,
}) => {
  const data = formData;
  const themeColors = useCustomColors();
  const links = data.links || [];

  React.useEffect(() => {
    if (isLoadingPages) return;

    let changed = false;
    const nextLinks = links.map(link => {
      let nextLink = link;
      if (isPlaceholderMenuLinkText(link.text)) {
        const title = pageTitleForMenuUrl(link.url, pages);
        if (title && title !== link.text) {
          nextLink = { ...nextLink, text: title };
          changed = true;
        }
      }

      if (!link.children?.length) return nextLink;

      const children = link.children.map(child => {
        if (!isPlaceholderMenuLinkText(child.text)) return child;
        const title = pageTitleForMenuUrl(child.url, pages);
        if (!title || title === child.text) return child;
        changed = true;
        return { ...child, text: title };
      });
      const childrenChanged = children.some((child, index) => child !== link.children?.[index]);
      if (!childrenChanged && nextLink === link) return link;

      return { ...nextLink, children };
    });

    if (changed) setFormData({ ...data, links: nextLinks });
  }, [isLoadingPages, pages, links, data, setFormData]);

  const handleLogoSave = (logoData: NavbarLogo) => {
    const fromLegacyBoth = isLegacyBothLogo(data.logo);
    const legacyText = data.logo?.text;

    let next: NavigationMenuData = { ...data, logo: logoData };
    if (fromLegacyBoth) {
      next = applyLegacyBothNavbarMigration(next, logoData, legacyText);
      if (siteId) markLegacyBothNoticeSeen(siteId);
    }

    setFormData(next);
    setIsLogoModalOpen(false);
  };

  const hasLogo = data.logo && (data.logo.text || data.logo.imageUrl || data.logo.cloudfrontUrl);

  const addLink = () => {
    if (links.length >= MAX_PARENTS) return;
    const newLink: NavigationMenuLink = {
      id: nextId('link'),
      text: 'New Link',
      url: '/',
      order: links.length,
      style: 'normal',
    };
    setFormData({ ...data, links: [...links, newLink] });
  };

  const updateLink = (index: number, updates: Partial<NavigationMenuLink>) => {
    const newLinks = [...links];
    newLinks[index] = {
      ...newLinks[index],
      ...updates,
      order: updates.order !== undefined ? updates.order : (newLinks[index].order ?? index),
    };
    setFormData({ ...data, links: newLinks });
  };

  const removeLink = (index: number) => {
    const newLinks = links
      .filter((_, i) => i !== index)
      .map((link, i) => ({
        ...link,
        order: link.order ?? i,
      }));
    setFormData({ ...data, links: newLinks });
  };

  const addChild = (parentIndex: number) => {
    const parent = links[parentIndex];
    const children = parent.children || [];
    if (children.length >= MAX_CHILDREN) return;
    const child: NavigationMenuChildLink = {
      id: nextId('child'),
      text: 'New sub link',
      url: '/',
      order: children.length,
    };
    updateLink(parentIndex, { children: [...children, child] });
  };

  const updateChild = (
    parentIndex: number,
    childIndex: number,
    updates: Partial<NavigationMenuChildLink>
  ) => {
    const children = sortSubLinks(links[parentIndex].children || []);
    const current = children[childIndex];
    if (!current) return;
    children[childIndex] = {
      ...current,
      ...updates,
      order: updates.order !== undefined ? updates.order : (current.order ?? childIndex),
    };
    updateLink(parentIndex, { children });
  };

  const removeChild = (parentIndex: number, childIndex: number) => {
    const children = sortSubLinks(links[parentIndex].children || [])
      .filter((_, i) => i !== childIndex)
      .map((child, i) => ({ ...child, order: child.order ?? i }));
    updateLink(parentIndex, { children });
  };

  const reorderChildren = (parentIndex: number, activeId: string, overId: string) => {
    const current = links[parentIndex].children || [];
    const children = reorderSubLinks(current, activeId, overId);
    if (children === current) return;
    updateLink(parentIndex, { children });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value || undefined },
    });
  };

  const colorFields = getColorFieldsForComponent('navigation-menu', themeColors);

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
        <h3 className="font-medium text-gray-900 flex items-center space-x-2">
          <Type size={18} />
          <span>Brand & Logo</span>
        </h3>

        {hasLogo ? (
          <LogoPreview
            data={data}
            onEdit={() => setIsLogoModalOpen(true)}
            onRemove={() => setFormData({ ...data, logo: undefined })}
            onSiteNameChange={value => setFormData({ ...data, siteName: value })}
          />
        ) : (
          <NoLogoSection
            siteName={data.siteName}
            onSiteNameChange={value => setFormData({ ...data, siteName: value })}
            onAddLogo={() => setIsLogoModalOpen(true)}
          />
        )}
      </div>

      <NavigationMenuLinksSection
        links={links}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        onAddLink={addLink}
        onUpdateLink={updateLink}
        onRemoveLink={removeLink}
        onAddChild={addChild}
        onUpdateChild={updateChild}
        onRemoveChild={removeChild}
        onReorderChild={reorderChildren}
        canAddLink={links.length < MAX_PARENTS}
      />

      <AlignmentSection
        alignment={data.alignment}
        sticky={data.sticky}
        onAlignmentChange={value =>
          setFormData({
            ...data,
            alignment: value as NavigationMenuData['alignment'],
          })
        }
        onStickyChange={value => setFormData({ ...data, sticky: value })}
      />

      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize menu colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | null | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
      />

      <LogoUploadModal
        isOpen={isLogoModalOpen}
        onClose={() => setIsLogoModalOpen(false)}
        onSave={handleLogoSave}
        siteId={siteId || ''}
        initialData={data.logo}
        isLoading={isLoading}
        pages={pages}
        isLoadingPages={isLoadingPages}
      />
    </div>
  );
};

interface NavigationMenuLinksSectionProps {
  links: NavigationMenuLink[];
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  canAddLink: boolean;
  onAddLink: () => void;
  onUpdateLink: (index: number, updates: Partial<NavigationMenuLink>) => void;
  onRemoveLink: (index: number) => void;
  onAddChild: (parentIndex: number) => void;
  onUpdateChild: (
    parentIndex: number,
    childIndex: number,
    updates: Partial<NavigationMenuChildLink>
  ) => void;
  onRemoveChild: (parentIndex: number, childIndex: number) => void;
  onReorderChild: (parentIndex: number, activeId: string, overId: string) => void;
}

const NavigationMenuLinksSection: React.FC<NavigationMenuLinksSectionProps> = ({
  links,
  pages,
  isLoadingPages,
  currentPageComponents,
  canAddLink,
  onAddLink,
  onUpdateLink,
  onRemoveLink,
  onAddChild,
  onUpdateChild,
  onRemoveChild,
  onReorderChild,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Navigation Links</label>
    <div className="space-y-3">
      {links.length === 0 && (
        <p className="text-sm text-gray-400 italic">No navigation links yet</p>
      )}
      {links.map((link, index) => {
        const children = sortSubLinks(link.children || []);
        return (
          <div key={link.id || index} className="space-y-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex space-x-2 items-center">
              <input
                type="text"
                value={link.text}
                onChange={e => onUpdateLink(index, { text: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="Link text"
                aria-label={`Header ${index + 1} text`}
              />
              <PageSelector
                value={link.url}
                onChange={url =>
                  onUpdateLink(index, menuLinkUpdateForUrl(link.text, link.url, url, pages))
                }
                pages={pages}
                isLoadingPages={isLoadingPages}
                currentPageComponents={currentPageComponents}
              />
              <input
                type="number"
                value={link.order ?? index}
                onChange={e => {
                  const numValue = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                  onUpdateLink(index, { order: !isNaN(numValue) ? numValue : 0 });
                }}
                className="w-16 px-2 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                aria-label={`Header ${index + 1} order`}
                min="0"
              />
              <button
                type="button"
                onClick={() => onRemoveLink(index)}
                className="p-2 text-red-600 hover:text-red-700"
                aria-label={`Remove ${link.text}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500">Style:</label>
              <select
                value={link.style || 'normal'}
                onChange={e =>
                  onUpdateLink(index, {
                    style: e.target.value as NavigationMenuLink['style'],
                  })
                }
                className="w-36 px-2 py-1 pr-8 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                aria-label={`Header ${index + 1} style`}
              >
                <option value="normal">Normal Link</option>
                <option value="button">Button (Solid)</option>
                <option value="outline">Button (Outline)</option>
              </select>
            </div>

            <NavigationMenuSubLinks
              parentLabel={link.text}
              links={children}
              pages={pages}
              isLoadingPages={isLoadingPages}
              currentPageComponents={currentPageComponents}
              canAdd={children.length < MAX_CHILDREN}
              onAdd={() => onAddChild(index)}
              onUpdate={(childIndex, updates) => onUpdateChild(index, childIndex, updates)}
              onRemove={childIndex => onRemoveChild(index, childIndex)}
              onReorder={(activeId, overId) => onReorderChild(index, activeId, overId)}
            />
          </div>
        );
      })}
    </div>
    <button
      type="button"
      onClick={onAddLink}
      disabled={!canAddLink}
      className={`${links.length > 0 ? 'mt-3' : 'mt-1'} w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1`}
    >
      <Plus size={16} />
      <span>Add Link</span>
    </button>
  </div>
);
